'use client'

// imports
import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import useThrottledCallback from '@/lib/hooks/useThrottledCallback';
import useWindowResize from '@/lib/hooks/useWindowResize';

// lib files
import { searchFiltersAtom } from '@/lib/atoms/search-filters';
import { searchResultsAtom } from '@/lib/atoms/search-results';

// components
import SearchPaginationList from '@/components/Search/SearchPagination/SearchPaginationList';
import SearchPaginationDirection from '@/components/Search/SearchPagination/SearchPaginationDirection';

// styles
import styles from './SearchPagination.module.scss';
import { SearchResults } from '@/lib/types/search/results';

interface SearchPaginationProps {
  depCurrentPage?: number;
  depTotalPages?: number;
}

/**
 * Get generic layout size based on window width.
 */
const getLayoutSize = (): 'small' | 'medium' | 'large' => {
  const width = window.innerWidth;
  if (width < 768) {
    return 'small';
  } else if (width < 1025) {
    return 'medium';
  }
  return 'large';
}

const defaultMaxPages = {
  small: {
    layoutTotal: 3,
    numOfBorderPages: 1
  },
  medium: {
    layoutTotal: 5,
    numOfBorderPages: 2
  },
  large: {
    layoutTotal: 9,
    numOfBorderPages: 4
  }
}

const SearchPagination = (props: SearchPaginationProps) => {
  const { depCurrentPage, depTotalPages } = props;
  const isStorybook = depCurrentPage && depTotalPages
  const [ layout, setLayout ] = useState(getLayoutSize());

  const [filters, setFilters] = useAtom(searchFiltersAtom)

  // searchResults is guaranteed to exist as this component isn't rendered by
  // <SearchLanding /> without results existing.
  let [searchResults] = useAtom(searchResultsAtom)
  searchResults = searchResults as SearchResults

  const [currentPage, setCurrentPage] = useState(depCurrentPage || filters.page)
  
  let totalPages

  if(isStorybook) {
    totalPages = depTotalPages
  } else {
    totalPages = searchResults.totalPages
  }

  const goToPage = useCallback((n: number) => {
    const limit = 10
    const start = (n * 10) - 10

    setFilters(previousFilters => {
      return { ...previousFilters, start, limit, page: n }
    })
    setCurrentPage(n)
  }, [setFilters, setCurrentPage])

  const onWindowResize = useWindowResize();

  // check if the ad is above the desired breakpoint on resize
  onWindowResize(useThrottledCallback(() => {
    setLayout(getLayoutSize());
  }));

  useEffect(() => {
    // scroll to the top of the page when the page changes
    window.scrollTo(0, 0);
  }, [currentPage]);

  return (
    <section className={styles.search_pagination}>
      {currentPage > 1 && (
        <SearchPaginationDirection
          value={currentPage - 1}
          direction="prev"
          label={layout === 'small' ? 'Prev' : 'Previous'}
          onClick={goToPage}
        />
      )}
      <SearchPaginationList
        currentPage={currentPage}
        maxPages={defaultMaxPages}
        layout={layout}
        totalPages={totalPages}
        onClick={goToPage}
      />

      {currentPage < totalPages && (
        <SearchPaginationDirection
          value={currentPage + 1}
          direction="next"
          label="Next"
          onClick={goToPage}
        />
      )}
    </section>
  );
};

export default SearchPagination;
